import {
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    forwardRef,
    Input,
    Output,
    ViewChild
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

// some ideas taken from https://embed.plnkr.co/YWBxkL/

@Component({
    standalone: false,
    selector: 'ep-checkbox',
    template: `
    <div
      class="{{ !hasParentWrapper ? 'unity-form-group checkbox mb-0' : '' }} {{ value == '?' ? 'indeterminate' : '' }}"
    >
      <div class="unity-form-row" [class.focus]="hasFocus">
        <label style="min-height: var(--height-input);">
          <input
            #checkboxEl
            [class]="tristate ? 'hasIndeterminate' : ''"
            type="checkbox"
            [disabled]="controlDisabled"
            [ngModel]="trueFalseValue"
            (change)="toggleValue()"
            (focus)="onFocus()"
            (blur)="onBlur()"
          />
          <span class="input-icon">
            <svg class="unity-icon unity-icon-checkmark">
              <use xlink:href="#unity-icon-checkmark"></use>
            </svg>
            <svg class="unity-icon unity-icon-minus">
              <use xlink:href="#unity-icon-minus"></use>
            </svg>
          </span>
          <span>{{ getLabel() }}</span>
        </label>
        <!-- <span style="color:transparent">.</span> -->
        <!-- IMPORTANT:  This span is necessary to make right allinged checkbox layout correct-->
      </div>
    </div>
  `,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => CheckboxComponent),
            multi: true
        }
    ]
})
export class CheckboxComponent implements ControlValueAccessor {
    @ViewChild('checkboxEl', { static: true })
    checkbox: ElementRef;

    @Input()
    label: string = null;

    @Input('disabled')
    controlDisabled: boolean = false; //is this the right way?

    @Input()
    hideLabel: boolean = false;

    @Input()
    hasParentWrapper: boolean = false;

    @Input()
    set format(value: string) {
        let format = 'TF';
        if (value && value != '') {
            format = value.toLocaleUpperCase();
        }
        switch (format) {
            case 'YN':
                this.checkedValue = 'Y';
                this.uncheckedValue = 'N';
                this.indeterminateValue = null;
                this.tristate = false;
                break;
            case 'YN?':
                this.checkedValue = 'Y';
                this.uncheckedValue = 'N';
                this.indeterminateValue = '?';
                this.tristate = true;
                break;
            case 'NY':
                this.checkedValue = 'N';
                this.uncheckedValue = 'Y';
                this.indeterminateValue = null;
                this.tristate = false;
                break;
            case 'NY?':
                this.checkedValue = 'N';
                this.uncheckedValue = 'Y';
                this.indeterminateValue = '?';
                this.tristate = true;
                break;
            case 'TF':
                this.checkedValue = true;
                this.uncheckedValue = false;
                this.indeterminateValue = null;
                this.tristate = false;
                break;
            case 'FT':
                this.checkedValue = false;
                this.uncheckedValue = true;
                this.indeterminateValue = null;
                this.tristate = false;
                break;
            default:
                console.error("Invalid 'format' parameter '" + format + "' passed to ep-checkbox control");
        }
    }

    @Output() blur = new EventEmitter();

    @Output() focus = new EventEmitter();

    tristate = false;
    checkedValue: any = true;
    uncheckedValue: any = false;
    indeterminateValue: any = null;
    hasFocus = false;

    innerValue: any = '';

    get trueFalseValue(): boolean {
        if (this.value == this.checkedValue) {
            return true;
        }
        if (this.value == this.uncheckedValue) {
            return false;
        }
        return false;
    }

    constructor(private cdRef: ChangeDetectorRef) { }

    toggleValue() {
        //tristate toggle cycle   checked->unchecked->indterminate->checked

        let newValue = this.checkedValue; //default next value  is checked

        if (this.innerValue == this.checkedValue) {
            newValue = this.uncheckedValue;
        } else if (this.tristate && this.innerValue == this.uncheckedValue) {
            newValue = this.indeterminateValue;
        }
        this.value = newValue;
        //=======
        //TODO: Test code for new ep-field w/ validation.
        //this.onTouchedCallback();
        //=======
    }

    setIndeterminateState() {
        this.checkbox.nativeElement.indeterminate =
            this.tristate && this.innerValue != this.checkedValue && this.innerValue != this.uncheckedValue;
    }

    onFocus() {
        this.hasFocus = true;
        this.focus.emit();
    }

    onBlur() {
        this.hasFocus = false;
        this.onTouchedCallback();
        this.blur.emit();
    }

    //***** BEGIN CONTROL VALUE ACCESSOR HANDLERS *****//

    //Placeholders for the callbacks which are later provided by the Control Value Accessor
    private onChangeCallback = (_: any) => { };
    private onTouchedCallback = () => { };

    //get accessor
    get value(): any {
        return this.innerValue;
    }

    //set accessor including call the onchange callback
    set value(v: any) {
        if (v !== this.innerValue) {
            this.innerValue = v;
            this.setIndeterminateState();
            this.onChangeCallback(v);
        }
    }

    //From ControlValueAccessor interface - Allows Angular to update the model
    writeValue(value: string): void {
        this.innerValue = value;
        this.setIndeterminateState();
        this.cdRef.detectChanges();
    }

    //From ControlValueAccessor interface - Allows Angular to register a function to call when the model changes.
    registerOnChange(fn: any) {
        this.onChangeCallback = fn;
    }

    //From ControlValueAccessor interface -  Allows Angular to register a function to call when the input has been touched.
    registerOnTouched(fn: any) {
        this.onTouchedCallback = fn;
    }

    //From ControlValueAccessor interface -  Allows Angular to disable the input.
    setDisabledState(isDisabled: boolean): void {
        this.controlDisabled = isDisabled;
    }

    getLabel() {
        if (this.hideLabel) {
            return '';
        }
        return this.label;
    }

    //***** END CONTROL VALUE ACCESSOR HANDLERS *****//
}
